// Suppression policies, tickets, persistent findings API
import { api } from './client';

// ============================================================================
// SUPPRESSION POLICY API
// ============================================================================

export const suppressionAPI = {
  // Get all suppression policies
  getAll: (params?: {
    scope?: string;
    type?: string;
    project_id?: number;
    is_active?: boolean;
    include_expired?: boolean;
    page?: number;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.scope) searchParams.append('scope', params.scope);
    if (params?.type) searchParams.append('type', params.type);
    if (params?.project_id) searchParams.append('project_id', params.project_id.toString());
    if (params?.is_active !== undefined) searchParams.append('is_active', params.is_active.toString());
    if (params?.include_expired !== undefined) searchParams.append('include_expired', params.include_expired.toString());
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    const queryString = searchParams.toString();
    return api.get(`/suppression-policies${queryString ? '?' + queryString : ''}`);
  },

  // Get a single suppression policy with audit trail
  get: (id: number) => {
    return api.get(`/suppression-policies/${id}`);
  },

  // Create a new suppression policy
  create: (policy: {
    name: string;
    description?: string;
    scope: string;
    scope_value?: string;
    project_id?: number;
    target_id?: number;
    match_criteria?: Record<string, any>;
    type: string;
    justification: string;
    accepted_by_role?: string;
    expires_at?: string;
    review_date?: string;
    external_ticket?: string;
    external_url?: string;
  }) => {
    return api.post('/suppression-policies', policy);
  },

  // Update a suppression policy
  update: (id: number, policy: Partial<{
    name: string;
    description: string;
    justification: string;
    expires_at: string;
    review_date: string;
    external_ticket: string;
    external_url: string;
    is_active: boolean;
  }>) => {
    return api.put(`/suppression-policies/${id}`, policy);
  },

  // Approve a risk acceptance policy
  approve: (id: number) => {
    return api.post(`/suppression-policies/${id}/approve`);
  },

  // Delete a suppression policy
  delete: (id: number) => {
    return api.delete(`/suppression-policies/${id}`);
  },

  // Get audit trail for a policy
  getAuditTrail: (id: number) => {
    return api.get(`/suppression-policies/${id}/audit`);
  },

  // Get client suppression report
  getClientReport: () => {
    return api.get('/client-suppressions/report');
  },

  // Apply a suppression to a specific result (creates fingerprint link)
  // 'fixed' type enables reappearance detection - alerts when vulnerability returns in future scans
  applyToResult: (resultId: number, suppression: {
    type: 'false_positive' | 'accepted_risk' | 'duplicate' | 'mitigated' | 're_rated' | 'fixed';
    justification: string;
    new_severity?: string;
    expires_at?: string;
    external_ticket?: string;
    external_url?: string;
    accepted_by_role?: string;
  }) => {
    return api.post(`/results/${resultId}/suppress`, suppression);
  },

  // Remove a suppression from a specific result
  removeFromResult: (resultId: number) => {
    return api.delete(`/results/${resultId}/suppress`);
  },

  // Get suppression info for a specific result
  getResultSuppression: (resultId: number) => {
    return api.get(`/results/${resultId}/suppression`);
  },
};

// ============================================================================
// SUPPORT TICKET API (Unified ticket system - support, vulnerability, exposure)
// ============================================================================

export const supportTicketAPI = {
  // Get all tickets with optional filtering
  getAll: (params?: {
    ticket_type?: 'support' | 'vulnerability' | 'exposure';
    status?: string;
    priority?: string;
    client_id?: number;
    project_id?: number;
    assigned_to_me?: boolean;
    assigned_contact_id?: number;
    created_by_me?: boolean;
    include_open?: boolean;
    include_closed?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.ticket_type) searchParams.append('ticket_type', params.ticket_type);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.priority) searchParams.append('priority', params.priority);
    if (params?.client_id) searchParams.append('client_id', params.client_id.toString());
    if (params?.project_id) searchParams.append('project_id', params.project_id.toString());
    if (params?.assigned_to_me) searchParams.append('assigned_to_me', 'true');
    if (params?.assigned_contact_id) searchParams.append('assigned_contact_id', params.assigned_contact_id.toString());
    if (params?.created_by_me) searchParams.append('created_by_me', 'true');
    if (params?.include_open !== undefined) searchParams.append('include_open', params.include_open.toString());
    if (params?.include_closed !== undefined) searchParams.append('include_closed', params.include_closed.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    const queryString = searchParams.toString();
    return api.get(`/tickets${queryString ? '?' + queryString : ''}`);
  },

  // Get a single ticket with replies
  get: (ticketId: number) => {
    return api.get(`/tickets/${ticketId}`);
  },

  // Create a new support ticket
  create: (ticket: {
    subject: string;
    description?: string;
    category?: 'support' | 'security' | 'billing' | 'feature' | 'bug' | 'other';
    priority?: 'low' | 'medium' | 'high' | 'critical';
    client_id?: number;
    project_id?: number;
    tags?: string;
  }) => {
    return api.post('/tickets', ticket);
  },

  // Update a ticket
  update: (ticketId: number, updates: {
    subject?: string;
    description?: string;
    category?: string;
    priority?: string;
    status?: string;
    assigned_to_id?: number;
    tags?: string;
    due_date?: string;
  }) => {
    return api.put(`/tickets/${ticketId}`, updates);
  },

  // Delete a ticket (admin only)
  delete: (ticketId: number) => {
    return api.delete(`/tickets/${ticketId}`);
  },

  // Add a reply to a ticket
  addReply: (ticketId: number, message: string, isInternal?: boolean) => {
    return api.post(`/tickets/${ticketId}/replies`, { message, is_internal: isInternal });
  },

  // Acknowledge a ticket (admin only)
  acknowledge: (ticketId: number) => {
    return api.post(`/tickets/${ticketId}/acknowledge`);
  },

  // Assign a ticket to a client contact
  assignToContact: (ticketId: number, contactId: number | null) => {
    return api.post(`/tickets/${ticketId}/assign-contact`, { contact_id: contactId });
  },

  // Get ticket statistics (admin only)
  getStats: () => {
    return api.get('/tickets/stats');
  },
};

// ============================================================================
// FINDING TICKET API
// ============================================================================

export const findingTicketAPI = {
  // Get all finding tickets
  getAll: (params?: {
    project_id?: number;
    status?: string;
    priority?: string;
    assigned_to_id?: number;
    sla_breached?: boolean;
    page?: number;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.project_id) searchParams.append('project_id', params.project_id.toString());
    if (params?.status) searchParams.append('status', params.status);
    if (params?.priority) searchParams.append('priority', params.priority);
    if (params?.assigned_to_id) searchParams.append('assigned_to_id', params.assigned_to_id.toString());
    if (params?.sla_breached !== undefined) searchParams.append('sla_breached', params.sla_breached.toString());
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    const queryString = searchParams.toString();
    return api.get(`/finding-tickets${queryString ? '?' + queryString : ''}`);
  },

  // Get tickets assigned to current user
  getMyTickets: () => {
    return api.get('/finding-tickets/my-tickets');
  },

  // Get a single finding ticket with comments and history
  get: (id: number) => {
    return api.get(`/finding-tickets/${id}`);
  },

  // Create a new finding ticket
  create: (ticket: {
    fingerprint?: string;
    result_id?: number;
    project_id: number;
    title: string;
    severity: string;
    vuln_type?: string;
    host?: string;
    port?: number;
    cve_id?: string;
    priority?: string;
    due_date?: string;
    description?: string;
    assigned_to_id?: number;
  }) => {
    return api.post('/finding-tickets', ticket);
  },

  // Update a finding ticket
  update: (id: number, updates: Partial<{
    status: string;
    priority: string;
    due_date: string;
    assigned_to_id: number;
    description: string;
    internal_note: string;
    resolution: string;
    resolution_type: string;
    external_ticket_id: string;
    external_ticket_url: string;
    external_system: string;
  }>) => {
    return api.put(`/finding-tickets/${id}`, updates);
  },

  // Add a comment to a ticket
  addComment: (id: number, comment: {
    message: string;
    is_internal?: boolean;
  }) => {
    return api.post(`/finding-tickets/${id}/comments`, comment);
  },

  getMTTRMetrics: (projectId?: number) => {
    const params = projectId ? `?project_id=${projectId}` : '';
    return api.get(`/finding-tickets/mttr${params}`);
  },
};

// ============================================================================
// PERSISTENT FINDINGS API (Carry-forward for firewall-filtered ports)
// ============================================================================

export const persistentFindingsAPI = {
  getAll: (params?: {
    project_id?: number;
    status?: string;
    severity?: string;
    target_id?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.project_id) searchParams.append('project_id', params.project_id.toString());
    if (params?.status) searchParams.append('status', params.status);
    if (params?.severity) searchParams.append('severity', params.severity);
    if (params?.target_id) searchParams.append('target_id', params.target_id.toString());
    const queryString = searchParams.toString();
    return api.get(`/persistent-findings${queryString ? '?' + queryString : ''}`);
  },

  getStats: (projectId: number) => {
    return api.get(`/persistent-findings/stats?project_id=${projectId}`);
  },

  close: (id: number) => {
    return api.put(`/persistent-findings/${id}/close`);
  },

  reactivate: (id: number) => {
    return api.put(`/persistent-findings/${id}/reactivate`);
  },

  bulkClose: (projectId: number, host: string, port?: number) => {
    return api.put('/persistent-findings/bulk-close', {
      project_id: projectId,
      host,
      port: port ?? undefined,
    });
  },

  pin: (scanResultId: number, comment?: string) => {
    return api.post('/persistent-findings/pin', {
      scan_result_id: scanResultId,
      comment: comment || '',
    });
  },

  unpin: (scanResultId: number) => {
    return api.put('/persistent-findings/unpin-from-scan', {
      scan_result_id: scanResultId,
    });
  },
};

// ============================================================================
// VULNERABILITY TICKET API (Enhanced ticket system linked to findings)
// ============================================================================

export const vulnerabilityTicketAPI = {
  // Create a ticket from a specific finding/scan result
  createFromResult: (data: {
    scan_result_id: number;
    assigned_contact_id?: number;
    due_date?: string;
    priority?: string;
    notes?: string;
  }) => {
    return api.post('/vulnerability-tickets', data);
  },

  // Batch create tickets for multiple findings
  batchCreate: (data: {
    scan_result_ids: number[];
    assigned_contact_id?: number;
    due_date?: string;
    priority?: string;
    group_by_host?: boolean; // Create one ticket per host instead of per finding
  }) => {
    return api.post('/vulnerability-tickets/batch', data);
  },

  // Create a ticket from an exposure event
  createFromEvent: (data: {
    exposure_event_id: number;
    source?: string; // "standard" or "enhanced"
    subject?: string; // Custom ticket subject (overrides default)
    description?: string; // Custom ticket description (overrides default)
    assigned_contact_id?: number;
    due_date?: string;
    priority?: string;
    notes?: string;
  }) => {
    return api.post('/exposure-tickets', data);
  },

  // Get tickets for a specific scan result
  getForResult: (resultId: number) => {
    return api.get(`/results/${resultId}/tickets`);
  },

  // Get tickets by vulnerability fingerprint
  getByFingerprint: (fingerprint: string) => {
    return api.get(`/fingerprints/${fingerprint}/tickets`);
  },

  // Assign a ticket to a client contact
  assignToContact: (ticketId: number, contactId: number | null) => {
    return api.post(`/tickets/${ticketId}/assign-contact`, { contact_id: contactId });
  },
};
